import React from 'react';
import ReactDOM from 'react-dom/client';
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import {CookiesProvider} from "react-cookie";
import { EsThemeProvider } from "./common/EsThemeContext";
import { EsTipHost } from "./common/EsPrime";

import EsParent from "./views/parent/EsParent";
import { AuthProvider, RequireAuth } from './views/parent/EsAuth';
// Every admin screen, the colour editor, the rule editor, and the codex, character, campaign, game,
// chat, rules, and talks pages are lazy: a few readers open them, and
// together they kept the entry chunk over webpack's 3 MiB budget (webpack.config.js, performance).
// esLazy puts each under its own Suspense and reloads once when a deploy has taken its chunk (EsLazy).
import { esLazy } from './common/EsLazy';
import { esFirstTouchCapture } from './common/EsFirstTouch';
const EsThemeEditor = esLazy( () => import( "./views/theme/EsThemeEditor" ) );
import EsThemeWho from './views/theme/EsThemeWho';
import EsErrorPage from "./views/util/EsErrorPage";
import EsHome from './views/home/EsHome';
import EsRegister from "./views/account/EsRegister";
import EsLogin from "./views/account/EsLogin";
import EsDash from "./views/dash/EsDash";
const EsAccountForm = esLazy( () => import( "./admin/EsAccountForm" ) );
const EsAccountList = esLazy( () => import( "./admin/EsAccountList" ) );
const EsAlertList = esLazy( () => import( "./admin/EsAlertList" ) );
const EsAlertForm = esLazy( () => import( "./admin/EsAlertForm" ) );
const EsAiAdmin = esLazy( () => import( "./admin/EsAiAdmin" ) );
import EsForgot from "./views/account/EsForgot";
import EsBirthdate from "./views/account/EsBirthdate";
import EsReset from "./views/account/EsReset";
import EsAccount from "./views/account/EsAccount";
import EsOptOut from "./views/account/EsOptOut";
import EsLegal from "./views/legal/EsLegal";
import EsPrivacy from "./views/legal/EsPrivacy";
import EsTerms from "./views/legal/EsTerms";
import EsLicenses from "./views/legal/EsLicenses";
import EsCrafterPage, { EsCrafterSwitch } from "./views/page/EsCrafterPage";
import { EsLocaleProvider } from "./common/EsLocale";

import {
    VIEW_HOME,
    ADMIN_ACCOUNT_FORM,
    ADMIN_ACCOUNT_LIST,
    ADMIN_ALERT_FORM,
    ADMIN_ALERT_LIST,
    ADMIN_COPYKEY_FORM,
    ADMIN_COPYKEY_LIST,
    ADMIN_LOCALE_FORM,
    ADMIN_LOCALE_LIST,
    ADMIN_TRANSLATOR_LIST,
    ADMIN_HISTORY_LIST,
    ADMIN_METRICS,
    ADMIN_AI,
    ADMIN_MAILING_FORM,
    ADMIN_MAILING_LIST,
    ADMIN_IMPORTER_FORM,
    ADMIN_IMPORTER_LIST,
    ADMIN_PACK_REVIEW,
    ADMIN_RULESYS_FORM,
    ADMIN_RULESYS_LIST,
    ADMIN_SUBS_LIST,
    ADMIN_EXPENSE_FORM,
    ADMIN_EXPENSE_LIST,
    ADMIN_STORYBOARD,
    ADMIN_PROFIT,
    ADMIN_REVENUE_LIST,
    VIEW_SUBSCRIBE,
    VIEW_SUBSCRIBE_DONE,
    VIEW_CONSOLE,
    VIEW_DEMO,
    VIEW_CHAT,
    VIEW_COMMONS_TABLES,
    POST_KEY_PARAM,
    VIEW_LOGIN,
    VIEW_REGISTER,
    VIEW_FORGOT,
    VIEW_BIRTHDATE,
    VIEW_RESET,
    VIEW_OPT_OUT,
    VIEW_ACCOUNT,
    VIEW_THEME,
    VIEW_TRANSLATE,
    VIEW_LINK,
    ID_PARAM,
    VIEW_ADMIN,
    ADMIN_ENTRY_LIST,
    ADMIN_CODEX_LIST,
    VIEW_WELCOME,
    ADMIN_ENTRY_FORM,
    ADMIN_SECTION_LIST,
    ADMIN_SECTION_FORM,
    ADMIN_COMMENT_LIST,
    ADMIN_COMMENT_FORM,
    ADMIN_TAG_LIST,
    ADMIN_TAG_FORM, VIEW_CODEX, KEY_PARAM, VIEW_CODEX_TOC, VIEW_CODEX_SEARCH, VIEW_CODEX_TAG_CLOUD, VIEW_CODEX_BOOKMARKS,
    VIEW_CODEX_SECTION, VIEW_CODEX_NEW, VIEW_LIBRARY, VIEW_PACKS,
    VIEW_CODEX_TAG_ENTRIES, TAG_PARAM,
    VIEW_LEGAL,
    VIEW_CHANGELOG,
    VIEW_PRIVACY,
    VIEW_TERMS,
    VIEW_LICENSES,
    VIEW_DEVELOPER_AGREEMENT,
    VIEW_PAGE,
    VIEW_CHARACTERS, VIEW_CHARACTER_SHEET, VIEW_CHARACTER_NEW,
    VIEW_CAMPAIGNS, VIEW_CAMPAIGN, STORY_SUFFIX,
    VIEW_RULES
} from "./common/EsConstants";
// Open to everyone, signed out too (Tim, 2026-10-31); a chunk of its own for its markdown renderer.
const EsChangelog = esLazy( () => import( "./views/changelog/EsChangelog" ) );
const EsHistoryList = esLazy( () => import( "./admin/EsHistoryList" ) );
// Lazy: chart.js is about 70 KB gzipped and only an admin opening Metrics needs it.
const EsMetrics = esLazy( () => import( "./admin/metrics/EsMetrics" ) );
// Lazy for the same reason: Profit draws with chart.js too.
const EsProfit = esLazy( () => import( "./admin/EsProfit" ) );
// The copykeys admin, its own chunk: a few readers, and the CLDR list and usage map with it.
const EsCopykeyList = esLazy( () => import( "./admin/EsCopykeyList" ) );
const EsCopykeyForm = esLazy( () => import( "./admin/EsCopykeyForm" ) );
const EsLocaleList = esLazy( () => import( "./admin/EsLocaleList" ) );
const EsLocaleForm = esLazy( () => import( "./admin/EsLocaleForm" ) );
const EsTranslatorList = esLazy( () => import( "./admin/EsTranslatorList" ) );
// /translate, its own chunk for the same few readers.
const EsTranslate = esLazy( () => import( "./views/translate/EsTranslate" ) );
// /link, where a person signs a game in by its code: a few readers, its own chunk.
const EsLink = esLazy( () => import( "./views/link/EsLink" ) );
// /legal/developer, the developer agreement: the plugin's few readers, its own chunk.
const EsDeveloperAgreement = esLazy( () => import( "./views/legal/EsDeveloperAgreement" ) );
const EsMailingList = esLazy( () => import( "./admin/EsMailingList" ) );
const EsMailingForm = esLazy( () => import( "./admin/EsMailingForm" ) );
const EsRulesysList = esLazy( () => import( "./admin/EsRulesysList" ) );
const EsImporterList = esLazy( () => import( "./admin/EsImporterList" ) );
const EsPackReviewList = esLazy( () => import( "./admin/EsPackReviewList" ) );
const EsImporterForm = esLazy( () => import( "./admin/EsImporterForm" ) );
const EsRulesysForm = esLazy( () => import( "./admin/EsRulesysForm" ) );
const EsSubsList = esLazy( () => import( "./admin/EsSubsList" ) );
const EsExpenseList = esLazy( () => import( "./admin/EsExpenseList" ) );
const EsExpenseForm = esLazy( () => import( "./admin/EsExpenseForm" ) );
const EsStoryboard = esLazy( () => import( "./admin/EsStoryboard" ) );
const EsRevenueList = esLazy( () => import( "./admin/EsRevenueList" ) );
import EsSubscribe from "./views/subscribe/EsSubscribe";
import EsSubscribeDone from "./views/subscribe/EsSubscribeDone";
const EsAdmin = esLazy( () => import( "./admin/EsAdmin" ) );
const EsEntryList = esLazy( () => import( "./admin/codex/EsEntryList" ) );
const EsCodexAdmin = esLazy( () => import( "./admin/codex/EsCodexAdmin" ) );
import EsWelcome from "./views/welcome/EsWelcome";
const EsEntryForm = esLazy( () => import( "./admin/codex/EsEntryForm" ) );
const EsSectionList = esLazy( () => import( "./admin/codex/EsSectionList" ) );
const EsSectionForm = esLazy( () => import( "./admin/codex/EsSectionForm" ) );
const EsCommentList = esLazy( () => import( "./admin/codex/EsCommentList" ) );
const EsCommentForm = esLazy( () => import( "./admin/codex/EsCommentForm" ) );
const EsTagList = esLazy( () => import( "./admin/codex/EsTagList" ) );
const EsTagForm = esLazy( () => import( "./admin/codex/EsTagForm" ) );
const EsCodex = esLazy( () => import( "./views/codex/EsCodex" ) );
const EsLibrary = esLazy( () => import( "./views/codex/EsLibrary" ) );
const EsLibraryRedirect = esLazy( () => import( "./views/codex/EsLibrary" ).then( ( pModule ) => ( { default: pModule.EsLibraryRedirect } ) ) );
const EsCodexToc = esLazy( () => import( "./views/codex/EsCodexToc" ) );
const EsCodexTagCloud = esLazy( () => import( "./views/codex/EsCodexTagCloud" ) );
const EsCodexBookmarks = esLazy( () => import( "./views/codex/EsCodexBookmarks" ) );
const EsCodexSearch = esLazy( () => import( "./views/codex/EsCodexSearch" ) );
const EsCodexTagEntries = esLazy( () => import( "./views/codex/EsCodexTagEntries" ) );
const EsCodexSection = esLazy( () => import( "./views/codex/EsCodexSection" ) );
const EsCharacters = esLazy( () => import( "./views/characters/EsCharacters" ) );
const EsCharacterSheet = esLazy( () => import( "./views/characters/EsCharacterSheet" ) );
const EsCharacterNew = esLazy( () => import( "./views/characters/EsCharacterNew" ) );
const EsCampaigns = esLazy( () => import( "./views/campaign/EsCampaigns" ) );
const EsCampaign = esLazy( () => import( "./views/campaign/EsCampaign" ) );
const EsAdventure = esLazy( () => import( "./views/adventure/EsAdventure" ) );
const EsCampaignStory = esLazy( () => import( "./views/campaign/EsCampaignStory" ) );
const EsAdventureWizard = esLazy( () => import( "./views/adventure/EsAdventureWizard" ) );
const EsGame = esLazy( () => import( "./views/game/EsGame" ) );
const EsDemo = esLazy( () => import( "./views/game/EsDemo" ) );
const EsCommons = esLazy( () => import( "./views/commons/EsCommons" ) );
const EsModeration = esLazy( () => import( "./views/moderation/EsModeration" ) );
const EsRuleEditor = esLazy( () => import( "./views/rules/EsRuleEditor" ) );
const EsCastReceiver = esLazy( () => import( "./views/cast/EsCastReceiver" ) );
import { EsAiGate, EsAlertsGate, EsCampaignBuilderGate, EsCharactersGate, EsCopykeysGate, EsCodexGate, EsChatGate, EsDashboardGate, EsFlagProvider, EsGuardianGate, EsRulesysGate, EsUnrealPluginGate } from "./common/EsFlags";
import { esMorePatch, ROOT_ROUTE_ID } from "./common/EsMore";
import { ADVENTURE_SUFFIX, VIEW_CAST, VIEW_MODERATION, WIZARD_SUFFIX } from "./common/EsConstants";

// The root route's id is EsMore's: Talks join as its children, from a lazy chunk, only for a
// reader esback opens them to (esMorePatch), so this file never names them. The changelog is
// not EsMore's since 2026-10-31 and is routed below.
const epRouter = createBrowserRouter([
    {
        id: ROOT_ROUTE_ID,
        path: '/',
        element: <EsParent />,
        errorElement: <EsErrorPage />,
        children: [
            { path: VIEW_HOME, element: <EsCrafterSwitch slug="home"><EsHome /></EsCrafterSwitch>, },
            { path: VIEW_REGISTER, element: <EsRegister />, },
            { path: VIEW_LOGIN, element: <EsLogin />, },
            { path: VIEW_FORGOT, element: <EsForgot />, },
            // Public: whoever reaches it has no session yet. See EsBirthdate.
            { path: VIEW_BIRTHDATE, element: <EsBirthdate />, },
            // ⚠️ Public, and it has to be: everybody who reaches it is somebody
            // who cannot log in. The ?token= in the mailed link is the whole
            // credential -- see EsReset, which refuses to draw the form without
            // one rather than posting an empty string at the server.
            { path: VIEW_RESET, element: <EsReset />, },
            // Public on purpose, and it must stay that way: the SRD attribution
            // this page carries is a condition of shipping the codex content,
            // and a licence notice behind a login is not a licence notice.
            { path: VIEW_LEGAL, element: <EsCrafterSwitch slug="legal"><EsLegal /></EsCrafterSwitch>, },
            // Public, and flag-free since 2026-10-31 (Tim: "allow changelog to show in that flag,
            // just not talks"). No RequireAuth: it never had one.
            { path: VIEW_CHANGELOG, element: <EsChangelog />, },
            { path: VIEW_CHANGELOG + '/:version', element: <EsChangelog />, },
            // Public, bare, and flag-free: Stripe's account settings link both, and they must
            // answer with nobody signed in and LaunchDarkly unreachable. See EsPolicy.
            { path: VIEW_PRIVACY, element: <EsPrivacy />, },
            { path: VIEW_TERMS, element: <EsTerms />, },
            // Public, bare, and flag-free for the same reason as /legal: a licence notice
            // behind a login is not a licence notice. See EsLicenses.
            { path: VIEW_LICENSES, element: <EsLicenses />, },
            // Public but flagged, unlike its neighbours: a draft until Tim's text lands, and
            // nobody can register a game to agree to it while the plugin is off.
            { path: VIEW_DEVELOPER_AGREEMENT, element: <EsUnrealPluginGate><EsDeveloperAgreement /></EsUnrealPluginGate>, },
            { path: VIEW_PAGE + "/:slug", element: <EsCrafterPage />, },
            // Signed out too: a visitor previews colours for the session.
            { path: VIEW_THEME, element: <EsThemeEditor />, },
            {
                path: VIEW_CONSOLE,
                element:
                    <RequireAuth>
                        <EsDash />
                    </RequireAuth>
            },
            // Your own account -- the nickname other players see, and your
            // password. RequireAuth and no flag gate: every endpoint behind it
            // answers 401 logged out, and there is no feature flag over the
            // account page because it is not a feature waiting to ship. It is the
            // privacy half of the identity change (identity-plan-2026-09-12
            // §5) -- the screen that lets somebody be called something other
            // than their email address in front of a table.
            {
                path: VIEW_ACCOUNT,
                element:
                    <RequireAuth>
                        <EsAccount />
                    </RequireAuth>
            },
            // ⚠️ NOT behind RequireAuth, and it does the same job itself one
            // branch later. Both halves matter:
            //
            // A REGISTERED reader must sign in, because an email gets FORWARDED
            // and a link that worked without a session would let whoever
            // received the forward opt the original reader out silently. Their
            // mailing carries no token, so EsOptOut redirects them to login.
            //
            // A MAILING-tier JOINER has no Cognito user and can never sign
            // in, so RequireAuth here would refuse exactly the people the link
            // exists for. Their mailing carries a token and EsOptOut posts it.
            //
            // Tim's calls, both on 2026-09-18. See EsOptOut and
            // EsAccountService.optOutByToken, which refuses a token on any row
            // that is not a joiner.
            {
                path: VIEW_OPT_OUT,
                element: <EsOptOut />
            },
            // ---- The codex, public ------------------------------------
            //
            // Every codex route -- these seven and the eight admin screens
            // below -- is wrapped in EsCodexGate, the rolloutEsCodex flag.
            // Off, it redirects to the home page rather than rendering, which
            // is what a bookmarked or pasted URL needs: esback refuses the
            // same flag's API calls (EsFlagFilter), so an ungated page would be
            // a frame over an endpoint answering "Feature not available".
            // Hiding the nav entry is not enough on its own; see EsFlags.js.
            //
            // Decided by Tim on 2026-09-01 (plan §5): the codex goes public.
            // These seven routes were each wrapped in RequireAuth, and that
            // wrapper was correct right up until it was not -- the codex API
            // answered 401 to everyone logged out, so a public route would have
            // shown a page frame over nothing.
            //
            // The backend opens the codex GETs as permitAll in the same change
            // (contract §9: entryList, entryFirst, entry/key/*, entrySearch,
            // entryListByTag/*, sectionList, sectionTree, section/key/*,
            // tagList, tagCloud, tagListByEntry/*). Reads only -- every
            // POST/PUT/PATCH/DELETE stays ROLE_ADMIN -- and comments stay
            // authenticated, which EsCodexComments handles by rendering nothing
            // for a logged-out reader.
            //
            // Opening the API alone would have changed nothing a visitor can
            // see. This is the other half of it.
            //
            // The stray commas that used to sit after each element -- <Suspense fallback={ null }><EsCodex
            // /></Suspense>, inside the JSX -- are gone with the wrappers. They were not
            // separators; inside an element they are a text node React renders,
            // so every one of these pages was drawing a comma of its own.
            { path: VIEW_CODEX + KEY_PARAM, element: <EsCodexGate><EsCodex /></EsCodexGate>, },
            { path: VIEW_CODEX_NEW, element: <EsCodexGate><EsCodex fresh /></EsCodexGate>, },
            { path: VIEW_PACKS, element: <EsCodexGate><EsLibrary /></EsCodexGate>, },
            { path: VIEW_PACKS + "/:pack", element: <EsCodexGate><EsLibrary /></EsCodexGate>, },
            { path: VIEW_PACKS + "/:pack" + KEY_PARAM, element: <EsCodexGate><EsLibrary /></EsCodexGate>, },
            { path: VIEW_LIBRARY, element: <EsLibraryRedirect />, },
            { path: VIEW_LIBRARY + "/:pack", element: <EsLibraryRedirect />, },
            { path: VIEW_LIBRARY + "/:pack" + KEY_PARAM, element: <EsLibraryRedirect />, },
            // Bare /view/codex is the CONTENTS, not the first entry. Opening
            // the codex should show what is in it -- the same thing opening a
            // book does -- rather than dropping the reader onto page one of
            // 1,203 with no sense of the shape of it. /view/codexToc still
            // resolves here too, so existing links keep working.
            //
            // The Start reading button on that page is what covers the reader
            // who does want page one, and it has to name the entry explicitly:
            // it used to navigate to this bare path and let EsCodexEntry
            // resolve the chain head, which after this change would have been
            // a link back to the page the button is on.
            { path: VIEW_CODEX, element: <EsCodexGate><EsCodexToc /></EsCodexGate>, },
            { path: VIEW_CODEX_TOC, element: <EsCodexGate><EsCodexToc /></EsCodexGate>, },
            { path: VIEW_CODEX_SEARCH, element: <EsCodexGate><EsCodexSearch /></EsCodexGate>, },
            { path: VIEW_CODEX_TAG_CLOUD, element: <EsCodexGate><EsCodexTagCloud /></EsCodexGate>, },
            // Bookmarks are an account's own, so unlike the public reads around it this sits
            // behind RequireAuth.
            { path: VIEW_CODEX_BOOKMARKS, element: <EsCodexGate><RequireAuth><EsCodexBookmarks /></RequireAuth></EsCodexGate>, },
            { path: VIEW_CODEX_TAG_ENTRIES + TAG_PARAM, element: <EsCodexGate><EsCodexTagEntries /></EsCodexGate>, },
            { path: VIEW_CODEX_SECTION + KEY_PARAM, element: <EsCodexGate><EsCodexSection /></EsCodexGate>, },
            // ---- end codex ---------------------------------------------

            // ---- Characters, behind rolloutEsCharacters -----------------
            //
            // Both wrappers on every route, and the order is the readable one
            // rather than a meaningful one: RequireAuth waits for esback to say
            // who is logged in, EsCharactersGate waits for LaunchDarkly, and
            // either rendering nothing is the correct first frame.
            //
            // RequireAuth is NOT optional here the way it became optional for
            // the codex. A codex entry is public rules text; a character is one
            // person's own row, and every endpoint behind these pages answers
            // 401 logged out. A page frame over a 401 is the exact thing the
            // codex change was undoing.
            //
            // The sheet takes ID_PARAM rather than KEY_PARAM: EsBean's key is
            // unique across the table, and two players are each entitled to a
            // character called Thorin.
            {
                path: VIEW_CHARACTERS,
                element:
                    <RequireAuth>
                        <EsCharactersGate><EsCharacters /></EsCharactersGate>
                    </RequireAuth>
            },
            {
                path: VIEW_CHARACTER_NEW,
                element:
                    <RequireAuth>
                        <EsCharactersGate><EsCharacterNew /></EsCharactersGate>
                    </RequireAuth>
            },
            {
                path: VIEW_CHARACTER_SHEET + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCharactersGate><EsCharacterSheet /></EsCharactersGate>
                    </RequireAuth>
            },
            // ---- end characters ------------------------------------------

            // ---- Campaigns, behind rolloutEsConsole --------------------
            //
            // NEW routes, so they get the gate: off, EsDashboardGate redirects
            // a bookmarked or pasted URL to the home page rather than drawing a
            // page frame over an API that EsFlagFilter is answering 404 to.
            //
            // VIEW_CONSOLE above gets NO gate, and the difference is the point.
            // That route already exists and the Dash nav entry is
            // unconditional, so gating it would redirect every logged-in reader
            // who clicked Dash. EsDash branches on the flag internally instead;
            // see the header of that file.
            //
            // The campaign page takes KEY_PARAM -- the slug -- composed here
            // rather than baked into the constant, exactly as VIEW_CODEX +
            // KEY_PARAM is: a constant carrying ":key" cannot build a link, and
            // building the links on the campaign cards is what it is for.
            {
                path: VIEW_CAMPAIGNS,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsCampaigns /></EsDashboardGate>
                    </RequireAuth>
            },
            {
                path: VIEW_CAMPAIGN + KEY_PARAM,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsCampaign /></EsDashboardGate>
                    </RequireAuth>
            },
            // The campaign builder, on the same slug, behind its own flag as well.
            {
                path: VIEW_CAMPAIGN + KEY_PARAM + ADVENTURE_SUFFIX,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsCampaignBuilderGate><EsAdventure /></EsCampaignBuilderGate></EsDashboardGate>
                    </RequireAuth>
            },
            // The party's story (rolloutEsCampaignBuilder, which the page asks itself), on the same slug.
            {
                path: VIEW_CAMPAIGN + KEY_PARAM + STORY_SUFFIX,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsCampaignStory /></EsDashboardGate>
                    </RequireAuth>
            },
            // The builder's wizard, on the same slug and behind the same gates.
            {
                path: VIEW_CAMPAIGN + KEY_PARAM + ADVENTURE_SUFFIX + WIZARD_SUFFIX,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsCampaignBuilderGate><EsAdventureWizard /></EsCampaignBuilderGate></EsDashboardGate>
                    </RequireAuth>
            },
            // The game console, on the same slug and behind the same gate. It
            // is part of the Dashboard -- EsFlagFilter's DASHBOARD_PREFIXES
            // already covers every endpoint behind it, because they all begin
            // "campaign" -- so it takes the Dashboard's wrapper rather than one
            // of its own.
            //
            // It has no flag of its own: rolloutEsGame was removed in
            // es-0.6.0, and rolloutEsConsole already answers for every
            // campaign path on esback's side.
            // That is deliberate: esfront bakes its flags in at build time and
            // this feature has never shipped, so the honest gate is the one on
            // the API, and EsGame draws "not yet available" when every endpoint
            // answers 404. Off, a GM who follows the link from the campaign
            // page is told the feature is not there -- which is what EsDash
            // does for the Dashboard, and for the same reason.
            {
                path: VIEW_CONSOLE + KEY_PARAM,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsGame /></EsDashboardGate>
                    </RequireAuth>
            },
            // The demo table, a stable address. No flag gate here: the
            // page asks esback, whose 404 (switchEsDemoMode off, or no table) it says as closed.
            {
                path: VIEW_DEMO,
                element:
                    <RequireAuth>
                        <EsDashboardGate><EsDemo /></EsDashboardGate>
                    </RequireAuth>
            },
            // ---- end campaigns -------------------------------------------
            // ---- Rules: the tree is the rail's Rules tab (T60), the Packs page with the
            // rulesystems in the rail; a fork's editor stays here, behind rolloutEsHomebrewEngine.
            { path: VIEW_RULES, element: <EsCodexGate><EsLibrary rulesys /></EsCodexGate>, },
            {
                path: VIEW_RULES + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsRulesysGate><EsRuleEditor /></EsRulesysGate>
                    </RequireAuth>
            },
            // ---- The Commons, the out-of-game chat, behind rolloutEsChat ----
            //
            // One screen bar (EsCommons) on all four: the Commons bare, a topic or a Message by
            // its key so a link can name one, and under rolloutEsLfg the tables and one table's
            // post, which open in Posts. Inside EsChatGate, which has waited for LaunchDarkly, so
            // an unanswered LFG flag never lets the landing redirect move a Tables URL to a
            // topic. The tables segment is static, so it outranks the topic's :id.
            {
                path: VIEW_CHAT,
                element:
                    <RequireAuth>
                        <EsChatGate><EsCommons /></EsChatGate>
                    </RequireAuth>
            },
            {
                path: VIEW_CHAT + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsChatGate><EsCommons /></EsChatGate>
                    </RequireAuth>
            },
            {
                // Guardian's moderation view (es-guardian): open reports beside squelch and unsquelch.
                path: VIEW_MODERATION,
                element:
                    <RequireAuth>
                        <EsGuardianGate><EsModeration /></EsGuardianGate>
                    </RequireAuth>
            },
            {
                path: VIEW_COMMONS_TABLES,
                element:
                    <RequireAuth>
                        <EsChatGate><EsCommons /></EsChatGate>
                    </RequireAuth>
            },
            {
                path: VIEW_COMMONS_TABLES + POST_KEY_PARAM,
                element:
                    <RequireAuth>
                        <EsChatGate><EsCommons /></EsChatGate>
                    </RequireAuth>
            },
            // Public: the Subscribe page is the pitch, and a visitor reads it before
            // there is an account to gate on; the page itself asks for a sign-in where
            // the paying starts (Tim, 2026-10-11: "subscribe is still gated"). The
            // checkout and the Done page stay behind RequireAuth.
            {
                path: VIEW_SUBSCRIBE,
                element: <EsSubscribe />
            },
            {
                path: VIEW_SUBSCRIBE_DONE,
                element:
                    <RequireAuth>
                        <EsSubscribeDone />
                    </RequireAuth>
            },
            {
                path: VIEW_ADMIN,
                element:
                    <RequireAuth>
                        <EsAdmin />
                    </RequireAuth>
            },
            // The only account screen. The CRUD screen that used to sit beside
            // it was removed on 2026-09-04 -- see the header of EsAccountList,
            // which absorbed the Add button and the per-row delete it carried,
            // and which took its name on 2026-09-20. ADMIN_ACCOUNT_FORM stays:
            // it is still where a row is edited and where a new one is made,
            // and this list is now the only way to it.
            {
                path: ADMIN_ACCOUNT_LIST,
                element:
                    <RequireAuth>
                        { /* No comma after the element. Inside JSX it is not a
                             separator -- it is a text node React renders, so
                             the page draws a stray comma of its own. See the
                             note above the codex routes; the older admin
                             routes on either side of this one still carry the
                             mistake. */ }
                        <EsAccountList />
                    </RequireAuth>
            },
            {
                path: ADMIN_ACCOUNT_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsAccountForm />
                    </RequireAuth>
            },
            // Alerts: es_alert's rows, a list and a form, behind switchEsAlerts with the routes it reads.
            {
                path: ADMIN_ALERT_LIST,
                element:
                    <RequireAuth>
                        <EsAlertsGate><EsAlertList /></EsAlertsGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_ALERT_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsAlertsGate><EsAlertForm /></EsAlertsGate>
                    </RequireAuth>
            },
            // Copykeys and locale groups, behind rolloutEsTranslations with the copy/admin API they read.
            {
                path: ADMIN_COPYKEY_LIST,
                element:
                    <RequireAuth>
                        <EsCopykeysGate><EsCopykeyList /></EsCopykeysGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_COPYKEY_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCopykeysGate><EsCopykeyForm /></EsCopykeysGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_LOCALE_LIST,
                element:
                    <RequireAuth>
                        <EsCopykeysGate><EsLocaleList /></EsCopykeysGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_LOCALE_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCopykeysGate><EsLocaleForm /></EsCopykeysGate>
                    </RequireAuth>
            },
            // Translator recruitment, behind rolloutEsTranslations with the translator/admin API it reads.
            {
                path: ADMIN_TRANSLATOR_LIST,
                element:
                    <RequireAuth>
                        <EsCopykeysGate><EsTranslatorList /></EsCopykeysGate>
                    </RequireAuth>
            },
            // A translator's queues: signed in, behind rolloutEsTranslations with the translate/ API.
            // Who may read which group is esback's answer; the page says so when it is no.
            {
                path: VIEW_TRANSLATE,
                element:
                    <RequireAuth>
                        <EsCopykeysGate><EsTranslate /></EsCopykeysGate>
                    </RequireAuth>
            },
            // A device's code is approved by a signed-in account, so RequireAuth: the login it
            // sends a stranger to runs the birthdate step before any session exists (EsAge),
            // and lands back here with the ?code= still on the address.
            {
                path: VIEW_LINK,
                element:
                    <RequireAuth>
                        <EsUnrealPluginGate><EsLink /></EsUnrealPluginGate>
                    </RequireAuth>
            },
            // Read-only, so there is no form route beside it. Not behind a
            // flag either: it reads an endpoint that is already ROLE_ADMIN, so
            // there is nothing here that can half-work.
            {
                path: ADMIN_HISTORY_LIST,
                element:
                    <RequireAuth>
                        <EsHistoryList />
                    </RequireAuth>
            },
            {
                path: ADMIN_METRICS,
                element:
                    <RequireAuth>
                        <EsMetrics />
                    </RequireAuth>
            },
            {
                path: ADMIN_AI,
                element:
                    <RequireAuth>
                        <EsAiGate><EsAiAdmin /></EsAiGate>
                    </RequireAuth>
            },
            // Mailings. A list and a form, and
            // behind no flag gate for the same reason History is not: the
            // endpoints behind them are already ROLE_ADMIN and already there,
            // so there is nothing here that can half-work.
            {
                path: ADMIN_MAILING_LIST,
                element:
                    <RequireAuth>
                        <EsMailingList />
                    </RequireAuth>
            },
            {
                path: ADMIN_MAILING_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsMailingForm />
                    </RequireAuth>
            },
            {
                path: ADMIN_RULESYS_LIST,
                element:
                    <RequireAuth>
                        <EsRulesysList />
                    </RequireAuth>
            },
            {
                path: ADMIN_RULESYS_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsRulesysForm />
                    </RequireAuth>
            },
            {
                path: ADMIN_SUBS_LIST,
                element:
                    <RequireAuth>
                        <EsSubsList />
                    </RequireAuth>
            },
            {
                path: ADMIN_EXPENSE_LIST,
                element:
                    <RequireAuth>
                        <EsExpenseList />
                    </RequireAuth>
            },
            {
                path: ADMIN_EXPENSE_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsExpenseForm />
                    </RequireAuth>
            },
            // The Storyboard (V194): the bare route lists the boards, the id opens one.
            {
                path: ADMIN_STORYBOARD,
                element:
                    <RequireAuth>
                        <EsStoryboard />
                    </RequireAuth>
            },
            {
                path: ADMIN_STORYBOARD + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsStoryboard />
                    </RequireAuth>
            },
            {
                path: ADMIN_REVENUE_LIST,
                element:
                    <RequireAuth>
                        <EsRevenueList />
                    </RequireAuth>
            },
            {
                path: ADMIN_PROFIT,
                element:
                    <RequireAuth>
                        <EsProfit />
                    </RequireAuth>
            },
            {
                path: ADMIN_PACK_REVIEW,
                element:
                    <RequireAuth>
                        <EsPackReviewList />
                    </RequireAuth>
            },
            {
                path: ADMIN_IMPORTER_LIST,
                element:
                    <RequireAuth>
                        <EsImporterList />
                    </RequireAuth>
            },
            {
                path: ADMIN_IMPORTER_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsImporterForm />
                    </RequireAuth>
            },
            // Behind RequireAuth and behind NO flag gate. It is the page for a
            // reader every gate has turned away, so gating it would be the one
            // redirect with nowhere left to send them.
            {
                path: VIEW_WELCOME,
                element:
                    <RequireAuth>
                        <EsWelcome />
                    </RequireAuth>
            },
            {
                path: ADMIN_CODEX_LIST,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsCodexAdmin /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_ENTRY_LIST,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsEntryList /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_ENTRY_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsEntryForm /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_SECTION_LIST,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsSectionList /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_SECTION_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsSectionForm /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_COMMENT_LIST,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsCommentList /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_COMMENT_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsCommentForm /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_TAG_LIST,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsTagList /></EsCodexGate>
                    </RequireAuth>
            },
            {
                path: ADMIN_TAG_FORM + ID_PARAM,
                element:
                    <RequireAuth>
                        <EsCodexGate><EsTagForm /></EsCodexGate>
                    </RequireAuth>
            },

            // A url this app does not have, rendered INSIDE the shell.
            //
            // The errorElement above already caught these, and that is why this
            // looks redundant and is not. An errorElement REPLACES the element
            // on its own route -- here that is EsParent -- so a mistyped link
            // took the nav bar down with it and left the reader on a dead end
            // with nothing to click. A 404 is the one error where the reader
            // has certainly done nothing wrong and most needs a way out.
            //
            // As a plain catch-all CHILD it renders through EsParent's Outlet,
            // so the shell stays. EsErrorPage reads useRouteError, which is
            // undefined when it is rendered as an element rather than from a
            // boundary; it treats that as the 404 it can only be, and both
            // helpers were already null-safe.
            //
            // The root errorElement stays for everything else. A loader that
            // throws is a real failure, possibly of the shell itself, and
            // replacing the whole page is the honest answer to it.
            { path: '*', element: <EsErrorPage /> },
        ],
    },
    // The Google Cast receiver, the page a TV loads: a sibling of the shell, not a child, so it
    // draws no nav bar and no footer and asks nobody to sign in (EsCastReceiver).
    { path: VIEW_CAST, element: <EsCastReceiver />, errorElement: <EsErrorPage /> },
], { patchRoutesOnNavigation: esMorePatch });


// Before the router can move the address: the landing's UTM and click IDs (EsFirstTouch).
esFirstTouchCapture();

// New Relic Browser, read straight off the DefinePlugin constants so that a build with
// no app (a laptop, CI, the AQA bundle) folds this to `if ( "" )` and webpack emits no
// agent chunk at all. Started before the first render, but async all the same: the first
// fetch or two of a page load may go out before the agent wraps fetch and go
// unrecorded. A failed chunk costs the reader nothing.
if ( process.env.NEW_RELIC_BROWSER_APP_ID && process.env.NEW_RELIC_BROWSER_LICENSE_KEY ) {
  import( './common/EsRumAgent' ).then( ( pModule ) => pModule.esRumStart() ).catch( () => {} );
}

// Theming, which stopped being a stylesheet at PrimeReact 11. This used to be
// two CSS imports -- primereact/resources/themes/lara-light-indigo/theme.css
// and primereact.min.css -- and neither path exists any more: a theme is now a
// preset object the provider resolves into CSS variables.
//
// THE PRESET AND ITS OPTIONS MOVED TO src/common/EsThemeContext.js, 2026-09-07,
// and this file no longer renders PrimeReactProvider at all. Both definitions
// are there with their reasoning intact -- the Lara-with-indigo preset, and the
// darkModeSelector / cssLayer options that took a day to get right.
//
// The move is what the theme menu needs. A colour is chosen at run time, and the
// only mechanism in v11 that survives a rerender is handing PrimeReactProvider a
// freshly defined preset as its `theme` prop -- @primeuix/themes'
// updatePrimaryPalette is undone by the next render of whatever provider holds
// the prop, and updateSurfacePalette does nothing at all under Lara. Both were
// probed rather than assumed; see test/EsPrimeProbe9.test.jsx. So the preset has
// to be state, and the component holding it has to be the one rendering the
// provider.
//
// EsThemeProvider is outermost of everything, above EsFlagProvider: it renders
// PrimeReactProvider, which every screen below needs, and it owns the `dark`
// class on <html> that the whole app's Tailwind dark: variant keys on.
const root = ReactDOM.createRoot( document.getElementById( 'root' ) );
root.render(
  // <React.StrictMode>
      <EsThemeProvider license={ process.env.PRIMEUI_LICENSE }>
          { /* Above AuthProvider on purpose: AuthProvider renders
               EsFlagIdentity, which needs a LaunchDarkly client above it and
               is what starts that client.
               Unconfigured -- a checkout with no LD_CLIENT_ID -- this is a
               passthrough that constructs no client at all, and every flag reads
               its default. See EsFlags.js. */ }
          <EsFlagProvider>
              <CookiesProvider>
                  <AuthProvider>
                      { /* The reader's locale for Crafter copy, under AuthProvider because a
                           signed-in account's own choice wins. Renders nothing itself. */ }
                      <EsLocaleProvider>
                          <EsThemeWho />
                          { /* Every data-tip in the app, drawn once. See EsPrime. */ }
                          <EsTipHost />
                          <RouterProvider router={epRouter} />
                      </EsLocaleProvider>
                  </AuthProvider>
              </CookiesProvider>
          </EsFlagProvider>
      </EsThemeProvider>
  // </React.StrictMode>
);

